import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import { dispatch, useSelector } from '../../../../redux/store';
import { archiveUserRole, createUserRole, getUserRoleById, getUserRoles, updateUserRole } from '../../../../redux/slices/hrRedux/UserManagementRedux/userRoleRedux';
import DataGridTable from '../../../../components/table/DataGridTable';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import Iconify from '../../../../components/Iconify';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { accessVerify, capitalize } from '../../../../utils/common';
import PermissionRequired from '../../../errorPages/permissionRequired';

const EMPTY_FORM = {
  id: null,
  name: '',
  code: '',
  description: '',
  permissionCodes: [],
  assignedUserCount: 0,
  status: 'active',
};

const PermissionSwitches = ({ permissionsList, selectedCodes, onToggle, disabledCodes = [], readOnly = false }) => {
  const groupedPermissions = useMemo(() => {
    return (permissionsList || []).reduce((groups, permission) => {
      const mainCategory = permission.mainCategory || 'other';
      const category = permission.category || 'general';
      if (!groups[mainCategory]) groups[mainCategory] = {};
      if (!groups[mainCategory][category]) groups[mainCategory][category] = [];
      groups[mainCategory][category].push(permission);
      return groups;
    }, {});
  }, [permissionsList]);

  return (
    <Stack spacing={1}>
      {Object.entries(groupedPermissions).map(([mainCategory, categories]) => (
        <Accordion key={mainCategory} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{capitalize(mainCategory)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {Object.entries(categories).map(([category, permissions]) => (
              <Box key={category} sx={{ mb: 2, p: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {capitalize(category)}
                </Typography>
                <Grid container spacing={1}>
                  {[...permissions]
                    .sort((a, b) => a.priority - b.priority)
                    .map((permission) => (
                      <Grid item xs={12} sm={6} md={4} key={permission.code}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={selectedCodes.includes(permission.code)}
                              disabled={readOnly || disabledCodes.includes(permission.code)}
                              onChange={() => onToggle(permission.code)}
                            />
                          }
                          label={permission.title || permission.code}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

export default function UserRoles() {
  const { themeStretch } = useSettings();
  const { roles, selectedRole, mutation, error } = useSelector((state) => state.userRoles);
  const { permissionsList } = useSelector((state) => state.common);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    dispatch(getUserRoles({ status: showArchived ? 'all' : 'active' }));
  }, [showArchived, mutation?.time]);

  useEffect(() => {
    if (!isAdd && selectedRole?.id === form.id) {
      setForm({
        id: selectedRole.id,
        name: selectedRole.name || '',
        code: selectedRole.code || '',
        description: selectedRole.description || '',
        permissionCodes: selectedRole.permissionCodes || [],
        assignedUserCount: Number(selectedRole.assignedUserCount || 0),
        status: selectedRole.status || 'active',
      });
    }
  }, [selectedRole, isAdd, form.id]);

  const manageRole = (role, mode) => {
    setValidationError('');
    setIsAdd(mode === 'add');
    if (mode === 'add') {
      setForm(EMPTY_FORM);
      setDialogOpen(true);
      return;
    }

    setForm({ ...role, permissionCodes: role.permissionCodes || [] });
    setDialogOpen(true);
    if (!role.permissionCodes) dispatch(getUserRoleById(role.id));
  };

  const updateForm = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  const togglePermission = (code) => {
    setForm((previous) => ({
      ...previous,
      permissionCodes: previous.permissionCodes.includes(code) ? previous.permissionCodes.filter((item) => item !== code) : [...previous.permissionCodes, code],
    }));
  };

  const submit = async () => {
    setValidationError('');
    if (!form.name.trim() || !form.code.trim()) {
      setValidationError('Role name and role code are required.');
      return;
    }
    if (form.permissionCodes.length === 0) {
      setValidationError('Select at least one permission for this role.');
      return;
    }
    if (!isAdd && form.assignedUserCount > 0 && !window.confirm('Updating this role will immediately change permissions for all assigned users. Continue?')) return;

    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      description: form.description.trim() || null,
      permissionCodes: [...new Set(form.permissionCodes)],
    };
    const response = isAdd ? await dispatch(createUserRole(payload)) : await dispatch(updateUserRole(form.id, payload));
    if (response?.status) setDialogOpen(false);
  };

  const archive = async (role) => {
    if (role.assignedUserCount > 0) {
      window.alert('This role cannot be archived while users are assigned to it.');
      return;
    }
    if (!window.confirm(`Archive the ${role.name} role?`)) return;
    dispatch(archiveUserRole(role.id));
  };

  const columns = [
    { accessorKey: 'name', header: 'Role Name' },
    { accessorKey: 'code', header: 'Code' },
    { accessorKey: 'description', header: 'Description' },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => <Chip size='small' label={cell.getValue()} color={cell.getValue() === 'active' ? 'success' : 'default'} sx={{ textTransform: 'capitalize' }} />,
    },
    { accessorKey: 'permissionCount', header: 'Permissions' },
    { accessorKey: 'assignedUserCount', header: 'Assigned Users' },
  ];

  if (!accessVerify('USER_ROLE_MANAGEMENT_PAGE')) return <PermissionRequired />;

  return (
    <Page title='User Roles'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='User Roles'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'HR', href: PATH_DASHBOARD.hr.root }, { name: 'User Roles' }]}
          action={
            <Stack direction='row' spacing={2} alignItems='center'>
              <FormControlLabel control={<Switch checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />} label='Show archived' />
              {accessVerify('USER_ROLE_ADD') && (
                <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageRole(EMPTY_FORM, 'add')}>
                  New User Role
                </Button>
              )}
            </Stack>
          }
        />

        {error?.message && <Alert severity='error' sx={{ mb: 2 }}>{error.message}</Alert>}

        <DataGridTable
          name='User Roles'
          data={roles || []}
          column={columns}
          enableRowSelection={false}
          isRowClickable={accessVerify('USER_ROLE_UPDATE')}
          onRowClick={(row) => accessVerify('USER_ROLE_UPDATE') && row.original.status === 'active' && manageRole(row.original, 'edit')}
          enableRowActions={accessVerify('USER_ROLE_ARCHIVE')}
          renderRowActionItems={(row) => [
            <Tooltip title={row.original.assignedUserCount > 0 ? 'Move assigned users before archiving' : 'Archive role'} key={`archive-${row.original.id}`}>
              <span>
                <IconButton
                  color='error'
                  disabled={row.original.status !== 'active' || row.original.assignedUserCount > 0 || !accessVerify('USER_ROLE_ARCHIVE')}
                  onClick={(event) => {
                    event.stopPropagation();
                    archive(row.original);
                  }}
                >
                  <Iconify icon='solar:archive-down-minimalistic-outline' width={20} height={20} />
                </IconButton>
              </span>
            </Tooltip>,
          ]}
        />
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth='xl'
        scroll='paper'
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>{isAdd ? 'Add User Role' : 'Edit User Role'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {validationError && <Alert severity='warning'>{validationError}</Alert>}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth label='Role Name' value={form.name} onChange={updateForm('name')} />
              <TextField fullWidth label='Role Code' value={form.code} onChange={updateForm('code')} disabled={!isAdd} helperText='Immutable after creation; use uppercase letters, numbers, and underscores.' />
            </Stack>
            <TextField fullWidth label='Description' value={form.description} onChange={updateForm('description')} multiline minRows={2} />
            {!isAdd && form.assignedUserCount > 0 && <Alert severity='info'>This role is assigned to {form.assignedUserCount} user(s). Saving changes synchronizes their permissions immediately.</Alert>}
            <PermissionSwitches
              permissionsList={permissionsList}
              selectedCodes={form.permissionCodes}
              onToggle={togglePermission}
              disabledCodes={accessVerify('SUPER_ADMIN') ? [] : ['SUPER_ADMIN']}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant='contained' onClick={submit} disabled={!isAdd && !accessVerify('USER_ROLE_UPDATE')}>
            {isAdd ? 'Create Role' : 'Save Changes'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
